import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckIcon from '@mui/icons-material/Check';
import CallMadeIcon from '@mui/icons-material/CallMade';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import dayjs from 'dayjs';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import ActionItemForm from './ActionItemForm';
import { decodeHtml } from '../../../MyActivities/MyCV/helpers/ProcessTemplate';
import { formatDateOnly } from '../../../../utils/dateOnly';

// Action Item Status Constants
export const ACTION_ITEM_STATUSES = {
  OPEN: 'Open',
  IN_PROGRESS: 'InProgress',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export const getActionStatusColor = (status) => {
  switch (status) {
    case ACTION_ITEM_STATUSES.OPEN:
      return '#F3F4F6';
    case ACTION_ITEM_STATUSES.IN_PROGRESS:
      return '#DBEAFE';
    case ACTION_ITEM_STATUSES.DONE:
      return '#DCFCE7';
    case ACTION_ITEM_STATUSES.CANCELLED:
      return '#FEE2E2';
    default:
      return '#F3F4F6';
  }
};

export const getActionStatusTextColor = (status) => {
  switch (status) {
    case ACTION_ITEM_STATUSES.OPEN:
      return '#6B7280';
    case ACTION_ITEM_STATUSES.IN_PROGRESS:
      return '#2563EB';
    case ACTION_ITEM_STATUSES.DONE:
      return '#16A34A';
    case ACTION_ITEM_STATUSES.CANCELLED:
      return '#DC2626';
    default:
      return '#6B7280';
  }
};

export const getActionStatusIcon = (status, size = 16) => {
  const iconProps = { sx: { fontSize: size } };
  switch (status) {
    case ACTION_ITEM_STATUSES.DONE:
      return <CheckIcon {...iconProps} sx={{ ...iconProps.sx, color: getActionStatusTextColor(status) }} />;
    case ACTION_ITEM_STATUSES.IN_PROGRESS:
      return <PlayArrowIcon {...iconProps} sx={{ ...iconProps.sx, color: getActionStatusTextColor(status) }} />;
    case ACTION_ITEM_STATUSES.CANCELLED:
      return <CallMadeIcon {...iconProps} sx={{ ...iconProps.sx, color: getActionStatusTextColor(status) }} />;
    case ACTION_ITEM_STATUSES.OPEN:
    default:
      return <RadioButtonUncheckedIcon {...iconProps} sx={{ ...iconProps.sx, color: getActionStatusTextColor(status) }} />;
  }
};

// Action Item Priority Constants
export const ACTION_ITEM_PRIORITIES = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const ACTION_ITEM_PRIORITY_OPTIONS = [
  { value: ACTION_ITEM_PRIORITIES.LOW, label: 'Low' },
  { value: ACTION_ITEM_PRIORITIES.MEDIUM, label: 'Medium' },
  { value: ACTION_ITEM_PRIORITIES.HIGH, label: 'High' },
];

export const getPriorityColor = (priority) => {
  switch (priority) {
    case ACTION_ITEM_PRIORITIES.HIGH:
      return '#FEE2E2';
    case ACTION_ITEM_PRIORITIES.MEDIUM:
      return '#FEF3C7';
    case ACTION_ITEM_PRIORITIES.LOW:
      return '#DCFCE7';
    default:
      return '#F3F4F6';
  }
};

export const getPriorityTextColor = (priority) => {
  switch (priority) {
    case ACTION_ITEM_PRIORITIES.HIGH:
      return '#DC2626';
    case ACTION_ITEM_PRIORITIES.MEDIUM:
      return '#D97706';
    case ACTION_ITEM_PRIORITIES.LOW:
      return '#16A34A';
    default:
      return '#6B7280';
  }
};

// Size configurations for variants
const SIZE_CONFIG = {
  full: {
    padding: 2,
    chipHeight: 20,
    chipFontSize: '10px',
    titleFontSize: 'inherit',
    titleColor: '#1F2937',
    captionFontSize: '11px',
    secondLineFontSize: '12px',
    secondLineAvatarSize: 16,
    avatarSize: 18,
    addedByAvatarSize: 16,
    formSpacing: 2,
    formControlMinWidth: 120,
    datePickerMinWidth: 150,
    arrowIconSize: 'medium',
    backgroundColor: 'white',
    expandedBackground: '#f5f7fa',
    borderColor: '#e0e0e0',
  },
  compact: {
    padding: 1.5,
    chipHeight: 18,
    chipFontSize: '9px',
    titleFontSize: '13px',
    titleColor: '#374151',
    captionFontSize: '10px',
    secondLineFontSize: '11px',
    secondLineAvatarSize: 14,
    avatarSize: 16,
    addedByAvatarSize: 14,
    formSpacing: 1.5,
    formControlMinWidth: 100,
    datePickerMinWidth: 130,
    arrowIconSize: 18,
    backgroundColor: '#FAFBFC',
    expandedBackground: '#F3F4F6',
    borderColor: '#E5E7EB',
  },
};

const ActionItemCard = ({
  actionItem,
  variant = 'full',
  isExpanded,
  isEditing,
  editingItem,
  onToggleExpand,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onEditChange,
  onScrollToNote,
  showFromNote = true,
  canEdit = true,
}) => {
  const config = SIZE_CONFIG[variant];

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${config.borderColor}`,
        borderRadius: '4px',
        mb: variant === 'full' ? 2 : 1,
        overflow: 'hidden',
        backgroundColor: config.backgroundColor,
      }}
    >
      {/* Action Item Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          p: config.padding,
          cursor: 'pointer',
          backgroundColor: isExpanded ? config.expandedBackground : config.backgroundColor,
          borderBottom: isExpanded ? `1px solid ${config.borderColor}` : 'none',
        }}
        onClick={onToggleExpand}
      >
        <Box sx={{ flex: 1 }}>
          {/* Line 1: Status icon + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {getActionStatusIcon(actionItem.status)}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: config.titleColor,
                fontSize: config.titleFontSize,
              }}
            >
              {actionItem.title}
            </Typography>
          </Box>
          {/* Line 2: Due date · Owner · Priority · Status · From Note */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 3 }}>
            {actionItem.deadlineAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: config.secondLineFontSize }}
              >
                Due {formatDateOnly(actionItem.deadlineAt, 'D MMM')}
              </Typography>
            )}
            {actionItem.deadlineAt && actionItem.ownerEmployee && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.secondLineFontSize }}>·</Typography>
            )}
            {actionItem.ownerEmployee && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmployeeAvatar employee={actionItem.ownerEmployee} size={config.secondLineAvatarSize} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.secondLineFontSize }}>
                  {actionItem.ownerEmployee.fullName}
                </Typography>
              </Box>
            )}
            {actionItem.ownerEmployee && actionItem.priority && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.secondLineFontSize }}>·</Typography>
            )}
            {actionItem.priority && (
              <Chip
                label={actionItem.priority}
                size="small"
                sx={{
                  backgroundColor: getPriorityColor(actionItem.priority),
                  color: getPriorityTextColor(actionItem.priority),
                  fontWeight: 500,
                  fontSize: config.chipFontSize,
                  height: config.chipHeight,
                }}
              />
            )}
            {actionItem.priority && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.secondLineFontSize }}>·</Typography>
            )}
            <Chip
              label={actionItem.status === ACTION_ITEM_STATUSES.IN_PROGRESS ? 'In Progress' : actionItem.status}
              size="small"
              sx={{
                backgroundColor: getActionStatusColor(actionItem.status),
                color: getActionStatusTextColor(actionItem.status),
                fontWeight: 500,
                fontSize: config.chipFontSize,
                height: config.chipHeight,
              }}
            />
            {showFromNote && actionItem.sourceMeetingNoteId && onScrollToNote && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.secondLineFontSize }}>·</Typography>
                <Typography
                  variant="caption"
                  onClick={(e) => {
                    e.stopPropagation();
                    onScrollToNote(actionItem.sourceMeetingNoteId);
                  }}
                  sx={{
                    fontSize: config.secondLineFontSize,
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#6B7280',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  From Note
                </Typography>
              </>
            )}
          </Box>
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(actionItem);
              }}
              sx={{ mr: 1 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            {isExpanded ? (
              <KeyboardArrowUpIcon sx={typeof config.arrowIconSize === 'number' ? { fontSize: config.arrowIconSize } : undefined} />
            ) : (
              <KeyboardArrowDownIcon sx={typeof config.arrowIconSize === 'number' ? { fontSize: config.arrowIconSize } : undefined} />
            )}
          </Box>
        )}
        {!canEdit && (
          isExpanded ? (
            <KeyboardArrowUpIcon sx={typeof config.arrowIconSize === 'number' ? { fontSize: config.arrowIconSize } : undefined} />
          ) : (
            <KeyboardArrowDownIcon sx={typeof config.arrowIconSize === 'number' ? { fontSize: config.arrowIconSize } : undefined} />
          )
        )}
      </Box>

      {/* Action Item Expanded Content */}
      {isExpanded && (
        <Box sx={{ p: config.padding }}>
          {isEditing ? (
            <ActionItemForm
              variant={variant}
              item={editingItem}
              onChange={onEditChange}
              onSave={onSaveEdit}
              onCancel={onCancelEdit}
              isNew={false}
              showHeader={false}
            />
          ) : (
            <Box>
              {actionItem.description && (
                <Box
                  sx={{
                    mb: variant === 'full' ? 2 : 1,
                    fontSize: variant === 'compact' ? '12px' : 'inherit',
                    '& p': { margin: 0 },
                    '& ul, & ol': { margin: '4px 0', paddingLeft: '1.5rem' },
                  }}
                  dangerouslySetInnerHTML={{ __html: decodeHtml(actionItem.description) }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: variant === 'full' ? 2 : 1 }}>
                <EmployeeAvatar employee={actionItem.addedByEmployee} size={config.addedByAvatarSize} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.captionFontSize }}>
                  Added by {actionItem.addedByEmployee?.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: config.captionFontSize }}>
                  • {dayjs(actionItem.createdAt).format(variant === 'full' ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY')}
                </Typography>
              </Box>
              {canEdit && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onStartEdit}
                  sx={variant === 'compact' ? { fontSize: '12px', py: 0.5 } : undefined}
                >
                  Edit
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ActionItemCard;
